import { Component, lazy, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { RefreshCw, Loader2 } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";

const boundedImport = (factory) => new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
        const error = new Error("Route module timed out");
        error.name = "ChunkLoadError";
        reject(error);
    }, 12000);
    Promise.resolve().then(factory).then(resolve, reject).finally(() => clearTimeout(timer));
});

// Retry transient chunk failures once. Keep module-evaluation errors visible.
export const lazyWithRecovery = (factory) => lazy(async () => {
    try { return await boundedImport(factory); }
    catch (error) {
        if (!/ChunkLoadError|Loading chunk|Failed to fetch dynamically imported module/i.test(`${error.name} ${error.message}`)) throw error;
        await new Promise(resolve => setTimeout(resolve, 500));
        return boundedImport(factory);
    }
});

const ReloadButton = () => {
    const { t } = useLang();
    return <button type="button" onClick={() => window.location.reload()} data-testid="route-reload-button" className="inline-flex items-center gap-2 rounded-lg bg-[#7C3AED] px-5 py-3 text-sm font-semibold text-white hover:bg-[#6D28D9] transition-colors"><RefreshCw size={16} />{t("dashReload")}</button>;
};

export const RouteLoader = () => {
    const { t } = useLang();
    const [slow, setSlow] = useState(false);
    useEffect(() => {
        const timer = setTimeout(() => setSlow(true), 15000);
        return () => clearTimeout(timer);
    }, []);
    return <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 bg-[#F8F9FC] text-center" role="status" data-testid="route-loader"><Loader2 size={32} className="animate-spin text-[#7C3AED]" /><p className="text-sm text-gray-600" data-testid="route-loading-message">{t(slow ? "dashRouteError" : "dashLoading")}</p>{slow && <ReloadButton />}</div>;
};

const RouteError = () => {
    const { t } = useLang();
    return <div className="min-h-screen flex flex-col items-center justify-center gap-5 p-6 bg-[#F8F9FC] text-center" role="alert" data-testid="route-load-error"><p className="max-w-md text-gray-700" data-testid="route-error-message">{t("dashRouteError")}</p><ReloadButton /></div>;
};

class RouteErrorBoundary extends Component {
    state = { failed: false };
    static getDerivedStateFromError() { return { failed: true }; }
    render() { return this.state.failed ? <RouteError /> : this.props.children; }
}

export const RouteRecovery = ({ children }) => {
    const location = useLocation();
    return <RouteErrorBoundary key={location.pathname}>{children}</RouteErrorBoundary>;
};